import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { Switch } from "@/components/ui/switch";
import { UserRound, Plus, Loader2, Pencil, Trash2, Clock, Ban, CalendarOff, X } from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { customFetch } from "@workspace/api-client-react";
import { extractApiErrorMessage } from "@/lib/api-error";

interface TimeRange {
  start: string;
  end: string;
}

interface DaySchedule {
  enabled: boolean;
  ranges: TimeRange[];
}

type ServiceSchedule = Record<string, DaySchedule>;
type SpecialtySchedules = Record<string, ServiceSchedule>;

interface Professional {
  id: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  specialtySchedules: any;
  allServices: boolean;
  generalSchedule: any;
  isActive: boolean;
}

interface Service {
  id: string;
  name: string;
  durationMinutes: number;
}

interface Block {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  reason: string | null;
}

const dayLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const timeOptions: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    timeOptions.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}
// Horário gravado fora da grade de 30 min (ex.: inícios fixos da Configuradora, faixa 08:00–10:40) entra na lista:
// sem ele o select abre em branco e a lojista não vê a faixa real (o valor salvo não muda).
const opcoesDeHora = (valor: string) => (timeOptions.includes(valor) || !valor ? timeOptions : [...timeOptions, valor].sort());

function normalizeDaySchedule(raw: any): DaySchedule {
  if (!raw) return { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] };
  if (raw.ranges && Array.isArray(raw.ranges)) {
    return { enabled: !!raw.enabled, ranges: raw.ranges };
  }
  return {
    enabled: !!raw.enabled,
    ranges: [{ start: raw.start || "08:00", end: raw.end || "18:00" }],
  };
}

function normalizeServiceSchedule(raw: any): ServiceSchedule {
  const result: ServiceSchedule = {};
  for (const dk of dayKeys) {
    result[dk] = normalizeDaySchedule(raw?.[dk]);
  }
  return result;
}

function normalizeSchedules(raw: any): SpecialtySchedules {
  if (!raw) return {};
  const result: SpecialtySchedules = {};
  for (const svcId of Object.keys(raw)) {
    result[svcId] = normalizeServiceSchedule(raw[svcId]);
  }
  return result;
}

export default function Professionals() {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const { data: professionals = [], isLoading } = useQuery<Professional[]>({
    queryKey: ["professionals"],
    queryFn: () => customFetch("/api/professionals"),
  });

  const { data: services = [] } = useQuery<Service[]>({
    queryKey: ["services-list"],
    queryFn: () => customFetch("/api/services?status=ativo"),
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Professional | null>(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [allServices, setAllServices] = useState(false);
  const [generalSchedule, setGeneralSchedule] = useState<ServiceSchedule>(() => {
    const gs: ServiceSchedule = {};
    dayKeys.forEach((k) => { gs[k] = { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] }; });
    return gs;
  });
  const [schedules, setSchedules] = useState<SpecialtySchedules>({});

  const [blocksOpen, setBlocksOpen] = useState(false);
  const [blocksProfId, setBlocksProfId] = useState("");
  const [blockForm, setBlockForm] = useState({ date: "", startTime: "08:00", endTime: "18:00", reason: "" });

  const { data: blocks = [] } = useQuery<Block[]>({
    queryKey: ["professional-blocks", blocksProfId],
    queryFn: () => customFetch(`/api/professionals/${blocksProfId}/blocks`),
    enabled: !!blocksProfId,
  });

  const assignedServiceIds = useMemo(() => Object.keys(schedules), [schedules]);
  const availableServices = useMemo(
    () => services.filter((s) => !assignedServiceIds.includes(s.id)),
    [services, assignedServiceIds],
  );

  const initGeneralSchedule = (): ServiceSchedule => {
    const gs: ServiceSchedule = {};
    dayKeys.forEach((k) => { gs[k] = { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] }; });
    return gs;
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", phone: "" });
    setAllServices(false);
    setGeneralSchedule(initGeneralSchedule());
    setSchedules({});
    setEditOpen(true);
  };

  const openEdit = (p: Professional) => {
    setEditing(p);
    setForm({ name: p.name, phone: p.phone || "" });
    setAllServices(!!p.allServices);
    setGeneralSchedule(p.generalSchedule ? normalizeServiceSchedule(p.generalSchedule) : initGeneralSchedule());
    setSchedules(normalizeSchedules(p.specialtySchedules));
    setEditOpen(true);
  };

  const addService = (serviceId: string) => {
    if (!serviceId || schedules[serviceId]) return;
    const week: ServiceSchedule = {};
    dayKeys.forEach((k) => {
      week[k] = { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] };
    });
    setSchedules((prev) => ({ ...prev, [serviceId]: week }));
  };

  const removeService = (serviceId: string) => {
    setSchedules((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
  };

  const toggleDay = (serviceId: string, dayKey: string) => {
    setSchedules((prev) => {
      const svc = { ...(prev[serviceId] || {}) };
      const day = { ...(svc[dayKey] || { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] }) };
      day.enabled = !day.enabled;
      if (day.enabled && day.ranges.length === 0) {
        day.ranges = [{ start: "08:00", end: "18:00" }];
      }
      svc[dayKey] = day;
      return { ...prev, [serviceId]: svc };
    });
  };

  const updateRange = (serviceId: string, dayKey: string, rangeIdx: number, field: "start" | "end", value: string) => {
    setSchedules((prev) => {
      const svc = { ...(prev[serviceId] || {}) };
      const day = { ...(svc[dayKey] || { enabled: true, ranges: [] }) };
      day.ranges = day.ranges.map((r, i) => (i === rangeIdx ? { ...r, [field]: value } : r));
      svc[dayKey] = day;
      return { ...prev, [serviceId]: svc };
    });
  };

  const addRange = (serviceId: string, dayKey: string) => {
    setSchedules((prev) => {
      const svc = { ...(prev[serviceId] || {}) };
      const day = { ...(svc[dayKey] || { enabled: true, ranges: [] }) };
      const lastRange = day.ranges[day.ranges.length - 1];
      day.ranges = [...day.ranges, { start: lastRange?.end || "13:00", end: "18:00" }];
      svc[dayKey] = day;
      return { ...prev, [serviceId]: svc };
    });
  };

  const removeRange = (serviceId: string, dayKey: string, rangeIdx: number) => {
    setSchedules((prev) => {
      const svc = { ...(prev[serviceId] || {}) };
      const day = { ...(svc[dayKey] || { enabled: true, ranges: [] }) };
      day.ranges = day.ranges.filter((_, i) => i !== rangeIdx);
      if (day.ranges.length === 0) day.enabled = false;
      svc[dayKey] = day;
      return { ...prev, [serviceId]: svc };
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name,
        phone: form.phone || null,
        allServices,
        generalSchedule: allServices ? generalSchedule : null,
        specialtySchedules: allServices ? {} : schedules,
      };
      if (editing) {
        return customFetch(`/api/professionals/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
      }
      return customFetch("/api/professionals", { method: "POST", body: JSON.stringify(body) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professionals"] });
      setEditOpen(false);
      toast({ title: editing ? "Profissional atualizado!" : "Profissional criado!" });
    },
    onError: (err: unknown) => toast({ title: "Erro", description: extractApiErrorMessage(err), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customFetch(`/api/professionals/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professionals"] });
      toast({ title: "Profissional removido" });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      customFetch(`/api/professionals/${id}`, { method: "PUT", body: JSON.stringify({ isActive }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["professionals"] }),
  });

  const addBlockMutation = useMutation({
    mutationFn: () =>
      customFetch(`/api/professionals/${blocksProfId}/blocks`, {
        method: "POST",
        body: JSON.stringify(blockForm),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-blocks", blocksProfId] });
      setBlockForm({ date: "", startTime: "08:00", endTime: "18:00", reason: "" });
      toast({ title: "Bloqueio adicionado" });
    },
    onError: (err: unknown) => toast({ title: "Erro", description: extractApiErrorMessage(err), variant: "destructive" }),
  });

  const removeBlockMutation = useMutation({
    mutationFn: (blockId: string) => customFetch(`/api/professionals/blocks/${blockId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-blocks", blocksProfId] });
      toast({ title: "Bloqueio removido" });
    },
  });

  const openBlocks = (profId: string) => {
    setBlocksProfId(profId);
    setBlockForm({ date: "", startTime: "08:00", endTime: "18:00", reason: "" });
    setBlocksOpen(true);
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profissionais</h1>
          <p className="text-muted-foreground mt-1">Gerencie sua equipe e horários por serviço.</p>
        </div>
        <Button onClick={openCreate} className="shadow-md shadow-primary/20">
          <Plus className="w-4 h-4 mr-2" /> Profissional
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : professionals.length === 0 ? (
        <Card className="p-12 text-center">
          <UserRound className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">Nenhum profissional cadastrado</p>
          <Button variant="outline" className="mt-4" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Adicionar</Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {professionals.map((p) => {
            const serviceCount = p.specialtySchedules ? Object.keys(p.specialtySchedules).length : 0;
            const serviceLabel = p.allServices ? "Todos os serviços" : `${serviceCount} serviço(s) configurado(s)`;
            return (
              <Card key={p.id} className={`p-5 transition-all ${!p.isActive ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary/15 text-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {p.avatarUrl ? <img src={p.avatarUrl} className="w-full h-full object-cover" /> : <UserRound className="w-6 h-6" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{p.name}</p>
                    {p.phone && <p className="text-sm text-muted-foreground">{p.phone}</p>}
                    <p className={`text-xs mt-1 ${p.allServices ? "text-primary font-medium" : "text-muted-foreground"}`}>{serviceLabel}</p>
                  </div>
                  <Switch checked={p.isActive} onCheckedChange={(v) => toggleActiveMutation.mutate({ id: p.id, isActive: v })} />
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openBlocks(p.id)}><CalendarOff className="w-3.5 h-3.5 mr-1" /> Bloqueios</Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={async () => { if (await confirm({ title: "Remover profissional?", confirmLabel: "Remover" })) deleteMutation.mutate(p.id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Profissional" : "Novo Profissional"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome do profissional" /></div>
              <div className="space-y-2"><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(opcional)" /></div>
            </div>

            {services.length > 0 && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Realiza todos os serviços</Label>
                  <Switch checked={allServices} onCheckedChange={setAllServices} />
                </div>
                {allServices && (
                  <p className="text-xs text-muted-foreground -mt-2">Este profissional será incluído em todos os serviços com o horário geral abaixo.</p>
                )}

                {allServices ? (
                  <Card className="p-4 space-y-3">
                    <h3 className="font-bold text-sm uppercase text-primary">Horário Geral</h3>
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Dias da semana</p>
                      <div className="flex flex-wrap gap-1.5">
                        {dayKeys.map((dk, i) => {
                          const isEnabled = generalSchedule[dk]?.enabled;
                          return (
                            <button
                              key={dk}
                              type="button"
                              onClick={() => {
                                setGeneralSchedule((prev) => {
                                  const day = { ...(prev[dk] || { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] }) };
                                  day.enabled = !day.enabled;
                                  if (day.enabled && day.ranges.length === 0) day.ranges = [{ start: "08:00", end: "18:00" }];
                                  return { ...prev, [dk]: day };
                                });
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                isEnabled ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"
                              }`}
                            >
                              {dayLabels[i]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {dayKeys.filter((dk) => generalSchedule[dk]?.enabled).length > 0 && (
                      <div className="space-y-3 pt-1">
                        {dayKeys.filter((dk) => generalSchedule[dk]?.enabled).map((dk) => {
                          const dayIdx = dayKeys.indexOf(dk);
                          const dayData = generalSchedule[dk]!;
                          return (
                            <div key={dk} className="border rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-sm">{dayLabels[dayIdx]}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setGeneralSchedule((prev) => {
                                      const day = { ...(prev[dk] || { enabled: true, ranges: [] }) };
                                      const lastRange = day.ranges[day.ranges.length - 1];
                                      day.ranges = [...day.ranges, { start: lastRange?.end || "13:00", end: "18:00" }];
                                      return { ...prev, [dk]: day };
                                    });
                                  }}
                                  className="text-xs text-primary font-medium hover:underline"
                                >
                                  + Intervalo
                                </button>
                              </div>
                              {dayData.ranges.map((range, ri) => (
                                <div key={ri} className="flex items-center gap-2">
                                  <select
                                    className="flex-1 border rounded-md p-2 text-sm bg-background"
                                    value={range.start}
                                    onChange={(e) => {
                                      setGeneralSchedule((prev) => {
                                        const day = { ...(prev[dk] || { enabled: true, ranges: [] }) };
                                        day.ranges = day.ranges.map((r, i) => (i === ri ? { ...r, start: e.target.value } : r));
                                        return { ...prev, [dk]: day };
                                      });
                                    }}
                                  >
                                    {opcoesDeHora(range.start).map((t) => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                  <span className="text-xs text-muted-foreground">às</span>
                                  <select
                                    className="flex-1 border rounded-md p-2 text-sm bg-background"
                                    value={range.end}
                                    onChange={(e) => {
                                      setGeneralSchedule((prev) => {
                                        const day = { ...(prev[dk] || { enabled: true, ranges: [] }) };
                                        day.ranges = day.ranges.map((r, i) => (i === ri ? { ...r, end: e.target.value } : r));
                                        return { ...prev, [dk]: day };
                                      });
                                    }}
                                  >
                                    {opcoesDeHora(range.end).map((t) => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                  {dayData.ranges.length > 1 && (
                                    <button type="button" onClick={() => {
                                      setGeneralSchedule((prev) => {
                                        const day = { ...(prev[dk] || { enabled: true, ranges: [] }) };
                                        day.ranges = day.ranges.filter((_, i) => i !== ri);
                                        if (day.ranges.length === 0) day.enabled = false;
                                        return { ...prev, [dk]: day };
                                      });
                                    }} className="text-red-500 hover:text-red-700 flex-shrink-0">
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                ) : (
                  <>
                <Label className="text-base font-semibold">Especialidades e Horários</Label>

                {availableServices.length > 0 && (
                  <select
                    className="w-full border rounded-lg p-2.5 text-sm bg-background text-muted-foreground"
                    value=""
                    onChange={(e) => { addService(e.target.value); }}
                  >
                    <option value="">Adicionar especialidade...</option>
                    {availableServices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}

                {assignedServiceIds.map((svcId) => {
                  const svc = services.find((s) => s.id === svcId);
                  if (!svc) return null;
                  const svcSchedule = schedules[svcId]!;
                  const enabledDays = dayKeys.filter((dk) => svcSchedule[dk]?.enabled);

                  return (
                    <Card key={svcId} className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-sm uppercase text-primary">{svc.name}</h3>
                        <button onClick={() => removeService(svcId)} className="text-red-500 hover:text-red-700"><X className="w-5 h-5" /></button>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Dias da semana</p>
                        <div className="flex flex-wrap gap-1.5">
                          {dayKeys.map((dk, i) => {
                            const isEnabled = svcSchedule[dk]?.enabled;
                            return (
                              <button
                                key={dk}
                                type="button"
                                onClick={() => toggleDay(svcId, dk)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                  isEnabled
                                    ? "bg-primary text-primary-foreground shadow-sm"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                                }`}
                              >
                                {dayLabels[i]}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {enabledDays.length > 0 && (
                        <div className="space-y-3 pt-1">
                          {enabledDays.map((dk) => {
                            const dayIdx = dayKeys.indexOf(dk);
                            const dayData = svcSchedule[dk]!;
                            return (
                              <div key={dk} className="border rounded-lg p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-sm">{dayLabels[dayIdx]}</span>
                                  <button
                                    type="button"
                                    onClick={() => addRange(svcId, dk)}
                                    className="text-xs text-primary font-medium hover:underline"
                                  >
                                    + Intervalo
                                  </button>
                                </div>
                                {dayData.ranges.map((range, ri) => (
                                  <div key={ri} className="flex items-center gap-2">
                                    <select
                                      className="flex-1 border rounded-md p-2 text-sm bg-background"
                                      value={range.start}
                                      onChange={(e) => updateRange(svcId, dk, ri, "start", e.target.value)}
                                    >
                                      {opcoesDeHora(range.start).map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <span className="text-xs text-muted-foreground">às</span>
                                    <select
                                      className="flex-1 border rounded-md p-2 text-sm bg-background"
                                      value={range.end}
                                      onChange={(e) => updateRange(svcId, dk, ri, "end", e.target.value)}
                                    >
                                      {opcoesDeHora(range.end).map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    {dayData.ranges.length > 1 && (
                                      <button type="button" onClick={() => removeRange(svcId, dk, ri)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                                        <X className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  );
                })}
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="pt-4 !flex-row gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name.trim()}>
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={blocksOpen} onOpenChange={setBlocksOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Bloqueios de Horário</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-2"><Label>Data</Label><DataBr value={blockForm.date} onChange={(v) => setBlockForm({ ...blockForm, date: v })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Início</Label><Input type="time" value={blockForm.startTime} onChange={(e) => setBlockForm({ ...blockForm, startTime: e.target.value })} /></div>
                <div className="space-y-2"><Label>Fim</Label><Input type="time" value={blockForm.endTime} onChange={(e) => setBlockForm({ ...blockForm, endTime: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Motivo (opcional)</Label><Input value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} /></div>
              <Button onClick={() => addBlockMutation.mutate()} disabled={addBlockMutation.isPending || !blockForm.date} className="w-full">
                {addBlockMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ban className="w-4 h-4 mr-2" /> Adicionar Bloqueio</>}
              </Button>
            </div>

            {blocks.length > 0 && (
              <div className="border-t pt-4 space-y-2">
                <Label className="text-sm font-semibold">Bloqueios existentes</Label>
                {blocks.map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium">{b.date.split("-").reverse().join("/")}</p>
                      <p className="text-muted-foreground">{b.startTime.slice(0, 5)} - {b.endTime.slice(0, 5)} {b.reason && `· ${b.reason}`}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeBlockMutation.mutate(b.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
